// Call-now overlay — staged 5-layer compose animation that shows the
// §9.6 composer running live. One layer activates per ~400ms, then the
// overlay transitions to a `connected` state. Replaces the old
// "spinner-then-connected" UX so operators see the prior-call composer
// at work.

import React, { useEffect, useState } from 'react';

import { Button } from '../../../ui';
import { PRIOR_CALL_LAYERS } from './composedPrompt';

const STEP_INTERVAL_MS = 400;

export interface ContactCallNowOverlayProps {
  open: boolean;
  contactName: string;
  onClose: () => void;
  /** Fires once after the 5-layer compose finishes (the dispatch moment). */
  onConnected: () => void;
}

const ContactCallNowOverlay: React.FC<ContactCallNowOverlayProps> = ({
  open,
  contactName,
  onClose,
  onConnected,
}) => {
  const [composeStep, setComposeStep] = useState(0);
  const [phase, setPhase] = useState<'composing' | 'connected'>('composing');

  useEffect(() => {
    if (!open) {
      setComposeStep(0);
      setPhase('composing');
      return;
    }
    setPhase('composing');
    setComposeStep(0);
    const timers: number[] = [];
    PRIOR_CALL_LAYERS.forEach((_layer, idx) => {
      const t = window.setTimeout(
        () => setComposeStep(idx + 1),
        STEP_INTERVAL_MS * (idx + 1),
      );
      timers.push(t);
    });
    const finalT = window.setTimeout(() => {
      setPhase('connected');
      onConnected();
    }, STEP_INTERVAL_MS * (PRIOR_CALL_LAYERS.length + 1));
    timers.push(finalT);
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [open, onConnected]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="call-now-title"
    >
      <div
        className="w-full max-w-lg bg-surface-1 border border-white/[0.16] rounded-ds-xl p-6 shadow-ds-e3 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
          {phase === 'connected'
            ? 'Connected'
            : 'Composing prior-call context'}
        </div>
        <div
          id="call-now-title"
          className="text-lg font-semibold text-slate-50 mt-1 mb-4"
        >
          {phase === 'connected' ? 'Agent is on the line' : `Calling ${contactName}…`}
        </div>

        <ul>
          {PRIOR_CALL_LAYERS.map((layer, idx) => {
            const done = composeStep > idx;
            const active = composeStep === idx + 1 && phase === 'composing';
            return (
              <li
                key={layer.id}
                className={`flex items-center gap-2.5 py-1.5 ${
                  done || active ? 'opacity-100' : 'opacity-[0.35]'
                }`}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                    done
                      ? 'bg-ok'
                      : active
                        ? 'bg-accent shadow-[0_0_8px_var(--accent)]'
                        : 'bg-white/10'
                  }`}
                />
                <span className="font-mono text-xs text-slate-400">{layer.id}</span>
                <span className="text-[13px] text-slate-50">{layer.label}</span>
                {done && (
                  <span className="ml-auto font-mono text-[10px] text-ok-text">
                    composed
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {phase === 'connected' && (
          <div className="mt-4 rounded-ds-md border border-ok-border bg-ok-tint/40 px-3.5 py-3 text-[12px] text-ok-text leading-relaxed">
            <div className="font-semibold text-slate-50 mb-1">
              Agent has full 5-layer context
            </div>
            Click outside to close.
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button intent="ghost" size="sm" onClick={onClose}>
            {phase === 'connected' ? 'Close' : 'Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContactCallNowOverlay;
